/**
 * Should the "Select Segments" step be skipped? Spec 25 / increment 05d.
 *
 * Puneet: "when there is just one segment in audience, skip the segment selection step use that by default
 * -> add for ai summit only please".
 *
 * Every summit audience is a single segment, so the step rendered one option and asked the attendee to click
 * it. That is a dead click in a demo someone is watching over their shoulder.
 *
 * Extracted as a pure function rather than written inline in `App.handleSelectAudience` because the rule has
 * three ways to be wrong — skipping when the plan does not allow it, skipping a 2-segment audience, and
 * skipping when the segment list failed to load and came back empty — and inline in a router callback none
 * of those is reachable by a test. Here each is one assertion.
 *
 * "AI summit only" is expressed as the PLAN FEATURE `autoSelectSingleSegment`, not an `isSummitUser` check:
 * the next cohort with single-segment audiences then needs no code change, and there is no second hardcoded
 * client list to drift out of step with the first. That drift is exactly how SmartSweets ended up with a
 * registry entry, no frontend boolean, and a blank dashboard.
 */

export interface SingleSegmentSkipInput {
  /** The plan's `autoSelectSingleSegment` feature. False for every plan but the summit cohort. */
  autoSelectSingleSegment: boolean;
  /** Segment ids belonging to the chosen audience. */
  segmentIds: number[];
}

export interface SingleSegmentSkipDecision {
  skip: boolean;
  /** The segment to preselect. Only set when `skip` is true. */
  segmentId: number | null;
}

export function decideSingleSegmentSkip(
  { autoSelectSingleSegment, segmentIds }: SingleSegmentSkipInput,
): SingleSegmentSkipDecision {
  // Not our cohort: behave exactly as before for everyone else. This is the branch that protects every
  // existing client, so it is checked first and is the one worth breaking a test over.
  if (!autoSelectSingleSegment) return { skip: false, segmentId: null };

  // EXACTLY one. Zero means the segment list is empty or failed to load — skipping then would advance to
  // "Review & Run" with nothing selected and produce a simulation against no personas, which is worse than
  // an extra click. Two or more is a real choice and belongs to the person, not to us.
  if (segmentIds.length !== 1) return { skip: false, segmentId: null };

  return { skip: true, segmentId: segmentIds[0] };
}
